// ============================================================================
// src/app/data/routeMutationCatalog.ts — the WRITE-side seam
// ============================================================================
// routeDataCatalog.ts owns reads and says so in its header: "no navigation, no
// migrations, NO WRITES". That sentence is correct and stays correct. What it
// leaves open is the other half of a cache: the app has no DECLARED
// invalidation contract, and queryClient.ts says in as many words that writing
// one is a separate, reviewable change on top of that foundation. This file is
// that change.
//
// WHY THIS FILE EXISTS — STATED CAREFULLY, BECAUSE THE OBVIOUS VERSION IS WRONG
//
// The tempting story is "retention is on, so surfaces now serve pre-mutation
// rows". THAT STORY IS FALSE, and an earlier draft of this header told it. It
// is worth writing down why, because anyone reading `gcTime: 5 * 60 * 1000` in
// isolation will reach for it again.
//
// gcTime is not paired with stale semantics; it is paired with `staleTime: 0`
// and `refetchOnMount: true`, and queryClient.ts is explicit that the PAIRING
// is the design: "staleTime stays 0 so the data is stale on arrival and every
// mount still refetches. That pairing IS the decision: stale-while-revalidate.
// Instant paint from cache, correctness restored a moment later."
//
// So a surface that unmounts and remounts refetches. There is no five-minute
// window of stale rows, and the header's warning about "a surface silently
// serving rows from before the mutation" describes retention adopted WITHOUT
// that pairing — which is not what shipped. Sign-out is covered too:
// AuthContext calls appQueryClient.clear().
//
// WHAT IS ACTUALLY MISSING, WHICH IS NARROWER AND STILL WORTH FIXING
//
// Freshness today comes from two mechanisms, and only the first is structural:
//
//   REMOUNT. Navigating away and back refetches. This one cannot be forgotten,
//   because nobody has to remember it.
//
//   A REMEMBERED `refetch()` AT THE WRITE SITE, spelled `loadAll` almost
//   everywhere. This one is hand-maintained, per call site, and NOTHING CHECKS
//   IT. That is the gap.
//
// Two consequences follow, both real and both bounded:
//
//   * A resource whose observer STAYS MOUNTED across the write does not refresh
//     unless something explicitly asks it to. There is no other trigger —
//     refetchOnWindowFocus, refetchOnReconnect and refetchInterval are all off
//     by deliberate choice. This is precisely why every write site grew an
//     `await loadAll()` in the first place: it is a manual patch for a missing
//     contract.
//   * A retained-but-unmounted resource paints its pre-write rows for ONE FRAME
//     on its next mount, before the refetch lands. A flash, not a wrong answer.
//
// And the remembered half is demonstrably incomplete, which is the point: a
// rename on the sheep batch RECORD page called `loadAll` for its own resource
// and for nothing else, while the batch INDEX, the sheep home tile and the
// production totals all read the row that changed. The hub's create called no
// refetch at all — it navigated away instead.
//
// A catalog beats twenty remembered refetches for one reason, and it is not
// tidiness: an entry DECLARES which resources its write invalidates, so a guard
// can derive the required set from the read catalog and check it. Remembering
// cannot be checked. Every claim in the paragraph above was found by that
// derivation rather than by reading the code hoping to notice.
//
// ---------------------------------------------------------------------------
// WHAT THIS FILE IS NOT
//
// IT IS NOT A SECOND HOME FOR WRITE LOGIC. There are roughly forty `src/lib/
// *Api.ts` modules that already wrap a write in a named, pure, testable
// function — weighInApi, todoApi, processingApi, commentsApi, pastureMapApi,
// processingBatchDeleteApi and so on. Those ARE a write seam and they are a
// good one. Re-implementing them here would be a redundant abstraction. An
// entry whose write is already owned by such a module CALLS it (see
// `sheep.batch.delete` below); an entry whose write is a bare inline PostgREST
// chain in a component owns it here, because there was no owner.
//
// IT IS NOT AN OFFLINE QUEUE, AND IT MUST NEVER BECOME A SECOND ONE. This app
// already queues writes for field users who lose signal: `useOfflineSubmit` and
// `useOfflineRpcSubmit` drain an IndexedDB queue with client-submission-id
// idempotency, retry budgets, a stuck-row modal and photo-blob replay. They own
// a SPECIFIC, REGISTERED population — the single-row form submissions declared
// in offlineForms.ts and offlineRpcForms.ts (fuel_supplies, poultry_dailys,
// cattle_dailys, sheep_dailys, pig_dailys, and the six submit_* RPCs). A
// mutation entry that named one of those tables would give the same write two
// owners, and the loser would be the queue: a submit that went out through
// Query instead of the queue is a submit that is DROPPED when the tractor
// drives out of wifi range.
//
// So the two compose by DISJOINTNESS rather than by layering, and the
// disjointness is a checked property, not a convention: the static guard
// derives the offline-owned table and RPC set from those two registries and
// fails by name if any entry here claims one. Nothing in this file needs to
// know the queue exists; it simply may not reach into its territory.
//
// IT IS NOT A CACHE-KEY BUILDER. Keys belong to resourceKey() in the read
// catalog, and an entry here names RESOURCE IDS, never key arrays. That is
// deliberate: resourceKey puts the resource id at index 0 precisely so "a
// prefix match on [resourceId] still selects every scope's entries for
// invalidation", and inventing a key array at a write site would be the same
// scope-forgetting defect the read catalog already forbids.
// ============================================================================

import {RESOURCE} from './routeDataCatalog.js';
import {deleteSheepProcessingBatch} from '../../lib/processingBatchDeleteApi.js';
import {runSheepBatchOperation} from '../../lib/sheepBatchOperationApi.js';
import {newSheepAnimalRequestId, runSheepAnimalOperation} from '../../lib/sheepAnimalOperationApi.js';
import type {ProcessingBatchDeleteOptions} from '../../lib/processingBatchDeleteApi.js';
import {newCattleRequestId, runCattleOperation} from '../../lib/cattleOperationsApi.js';
import {newRuminantOptionRequestId, runRuminantOptionOperation} from '../../lib/ruminantOptionOperationApi.js';
import type {RuminantOptionRow} from '../../lib/ruminantOptionOperationApi.js';
import {runProjectJobOperation} from '../../lib/projectJobsApi.js';
import type {ProjectJobOpRequest} from '../../lib/projectJobsApi.js';
import type {DailyEditFenceInput, DailyEditOutcome} from '../../lib/dailyReportEditOperationApi.js';
// The client type is owned by src/lib/supabaseTypes.ts. A second
// '@supabase/supabase-js' specifier anywhere in the repository breaks the
// single-owner static guard, which counts TEXT — an erased `import type` still
// breaks it. Same note as routeResourceQuery.ts, same reason.
import type {RpcEnvelope, SupabaseClient} from '../../lib/supabaseTypes.js';

export const MUTATION = Object.freeze({
  SHEEP_BATCH_CREATE: 'sheep.batch.create',
  SHEEP_BATCH_PATCH: 'sheep.batch.patch',
  SHEEP_BATCH_DELETE: 'sheep.batch.delete',
  CATTLE_BATCH_CREATE: 'cattle.batch.create',
  CATTLE_BATCH_PATCH: 'cattle.batch.patch',
  CATTLE_CALVING_CREATE: 'cattle.calving.create',
  SHEEP_LAMBING_CREATE: 'sheep.lambing.create',
  SHEEP_ANIMAL_CREATE: 'sheep.animal.create',
  SHEEP_ANIMAL_PATCH: 'sheep.animal.patch',
  CATTLE_ORIGIN_CREATE: 'cattle.origin.create',
  CATTLE_ANIMAL_CREATE: 'cattle.animal.create',
  CATTLE_ANIMAL_PATCH: 'cattle.animal.patch',
  EQUIPMENT_MAINTENANCE_CREATE: 'equipment.maintenance.create',
  EQUIPMENT_MAINTENANCE_PATCH: 'equipment.maintenance.patch',
  EQUIPMENT_MATERIAL_CLEAR_CREATE: 'equipment.materialClear.create',
  PROJECT_JOB_OPERATION: 'projects.job.operation',
  POULTRY_DAILY_PATCH: 'poultry.daily.patch',
  LAYER_DAILY_PATCH: 'layer.daily.patch',
  EGG_DAILY_PATCH: 'egg.daily.patch',
  PIG_DAILY_PATCH: 'pig.daily.patch',
  CATTLE_DAILY_PATCH: 'cattle.daily.patch',
  SHEEP_DAILY_PATCH: 'sheep.daily.patch',
});

// ---------------------------------------------------------------------------
// Failure shape
// ---------------------------------------------------------------------------
// The read catalog's `unwrap` throws `${label}: ${message}`, because a read
// failure is rendered by a shared error surface that wants to say WHICH read
// broke. A write failure is different: it is rendered by the call site's own
// inline notice, whose text the operator already knows ("Status save failed:
// …"), and which today concatenates the RAW PostgREST message.
//
// So this one throws the raw message with NO label prefix. That is not
// sloppiness — it is what keeps a conversion byte-identical in what the
// operator reads. Adding a prefix here would silently reword every notice on
// every converted surface, which is exactly the class of invisible change a
// write-path lane must not make.
//
// ---------------------------------------------------------------------------
// WHY THE THROWN ERROR CARRIES `code`, `details` AND `hint`
//
// The first draft threw `new Error(error.message)` and stopped there. That
// discarded the rest of what PostgREST returned, and the omission was not a
// decision — nothing above argues for it, because there is no argument for it.
// It cost a conversion immediately.
//
// A PostgREST failure is NOT always a failure to the surface that issued it.
// EquipmentMaintenanceModal inserts with a stable `client_submission_id` and
// mig 086's unique index behind it, precisely so a double-tap collapses to a
// no-op instead of a duplicate maintenance row; the modal reads SQLSTATE
// **23505** off `error.code` and reports SUCCESS. HomeDashboard's material
// clear does the same for its own unique key. Those call sites are not doing
// something exotic — idempotency-by-unique-index is the standard way this app
// makes a retryable write safe, and the offline queue is built on it too.
//
// A seam that drops `code` therefore does not merely lose diagnostics: it makes
// every idempotent write in the app UNCONVERTIBLE, because the one field that
// distinguishes "already saved" from "actually broken" is gone by the time the
// call site sees the rejection. Matching on the message string instead is not
// an answer — that text is Postgres's, is localisable, and is exactly the kind
// of load-bearing string-sniffing a typed error code exists to retire.
//
// The addition is strictly additive, and that is the property that keeps the
// PR#203 parity claim intact: `message` is UNCHANGED, so every notice already
// rendered through `writeErrorMessage` still reads byte-for-byte the same. Only
// fields that did not exist before appear, and only when PostgREST supplied
// them — an absent `code` stays absent rather than becoming the string
// "undefined".
type PostgrestErrorLike = {message?: string; code?: string; details?: string; hint?: string};
type WriteResponse<Row> = {data?: Row | null; error?: PostgrestErrorLike | null};

/**
 * What a catalog write rejects with.
 *
 * A plain `Error` with the PostgREST fields attached rather than an `Error`
 * SUBCLASS, deliberately. A subclass would invite `instanceof`, and this
 * repository ships a code-split bundle where a class identity can differ
 * between chunks; the members below are checked by presence, which cannot
 * develop that failure mode. Call sites narrow with `writeErrorCode`.
 */
export type RouteWriteError = Error & {
  code?: string;
  details?: string;
  hint?: string;
};

function unwrapWrite<Row>(res: WriteResponse<Row>): Row | null {
  const error = res.error;
  if (error) {
    // The message is composed exactly as it always was. Nothing below touches
    // it; the fields are additions alongside it.
    const thrown = new Error(error.message || String(error)) as RouteWriteError;
    if (error.code !== undefined) thrown.code = error.code;
    if (error.details !== undefined) thrown.details = error.details;
    if (error.hint !== undefined) thrown.hint = error.hint;
    throw thrown;
  }
  return res.data ?? null;
}

/**
 * The SQLSTATE a rejection carries, or null.
 *
 * A helper rather than a documented cast, because the alternative at a call
 * site is `String((err as {code?: string}).code) === '23505'` — which is true
 * for the STRING "undefined" as readily as for a real code, and which every
 * converted surface would have to re-write correctly. One reading, one place.
 *
 * Returns null rather than '' for an absent code so `=== '23505'` cannot
 * accidentally hold, and tolerates a non-Error rejection because `run` is
 * allowed to reject with whatever the wrapped write rejected with.
 */
export function writeErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const code = (err as RouteWriteError).code;
  return typeof code === 'string' && code !== '' ? code : null;
}

// ---------------------------------------------------------------------------
// sheep processing batches — the pilot domain
// ---------------------------------------------------------------------------
// Chosen because it is the smallest domain in the app that exercises all three
// write kinds against catalog-backed reads: an INSERT on the index, an UPDATE
// on the record page, and a DELETE that is already owned by a SECURITY DEFINER
// RPC wrapper. See the lane report for the full selection argument.

/**
 * MIGRATION 241: a new batch is ONE `batch.create` operation. The server builds
 * the row (planned, no sheep, no totals) from the id, the name and the planned
 * date; nothing else is the caller's to choose. `requestId` is one per attempt
 * (a re-allocated name is a new attempt); a lost reply is retried inside the
 * runner with the same id.
 */
export type SheepBatchCreateVars = {
  requestId: string;
  batchId: string;
  name: string;
  plannedProcessDate: string;
};

/**
 * MIGRATION 241: the record page's writes are three operations on one entry --
 * `batch.update` (name, the two dates, processing_cost, notes), `batch.status`
 * (with the actual date when completing) and `batch.weight` (ONE sheep's cell;
 * the server recomputes the totals). Each carries the value the page READ as
 * `expected`, so a stale page is refused instead of overwriting.
 */
export type SheepBatchPatchVars = {
  requestId: string;
  operation: 'batch.update' | 'batch.status' | 'batch.weight';
  batchId: string;
  sheepId?: string;
  expected: Record<string, unknown>;
  change: Record<string, unknown>;
};

export type SheepBatchDeleteVars = ProcessingBatchDeleteOptions;

async function writeSheepBatchCreate(
  sb: SupabaseClient,
  {requestId, batchId, name, plannedProcessDate}: SheepBatchCreateVars,
): Promise<null> {
  if (!requestId || !batchId) throw new Error('sheep.batch.create: requestId and batchId are required');
  await runSheepBatchOperation(sb, {
    requestId,
    operation: 'batch.create',
    target: {batch_id: batchId},
    change: {name, planned_process_date: plannedProcessDate},
  });
  return null;
}

async function writeSheepBatchPatch(
  sb: SupabaseClient,
  {requestId, operation, batchId, sheepId, expected, change}: SheepBatchPatchVars,
): Promise<null> {
  if (!requestId || !batchId) throw new Error('sheep.batch.patch: requestId and batchId are required');
  if (!change || typeof change !== 'object') throw new Error('sheep.batch.patch: a change is required');
  await runSheepBatchOperation(sb, {
    requestId,
    operation,
    target: operation === 'batch.weight' ? {batch_id: batchId, sheep_id: String(sheepId || '')} : {batch_id: batchId},
    expected,
    change,
  });
  return null;
}

/**
 * The ONE entry that does not own its own write.
 *
 * `delete_sheep_processing_batch` is a transactional SECDEF RPC (migration 100)
 * and processingBatchDeleteApi is its client owner. Note what it does NOT do:
 * it never throws. A transport or permission failure comes back as
 * `{ok: false, reason: 'rpc_error', error}` and a business-rule block as
 * `{ok: false, reason: …}`, and SheepBatchPage branches on `del.ok`.
 *
 * That envelope is returned UNCHANGED. Converting it into a throw so it would
 * match the other two entries would be a silent behaviour change inside a
 * delete path — the page's blocked-detach summary notice reads `del.reason`,
 * and a throw would route it to a generic catch instead. The catalog adapts to
 * the write it wraps; the write does not adapt to the catalog.
 *
 * The consequence for invalidation is written down rather than left implicit:
 * because a refused delete resolves rather than rejects, the declared
 * invalidations DO run on `{ok: false}`. That is harmless — nothing changed
 * server-side, so a marked-stale entry refetches identical rows — and it is
 * strictly safer than the alternative, which would be to inspect an envelope
 * this layer does not own.
 */
async function writeSheepBatchDelete(sb: SupabaseClient, vars: SheepBatchDeleteVars): Promise<RpcEnvelope> {
  return deleteSheepProcessingBatch(sb, vars);
}

// ---------------------------------------------------------------------------
// cattle processing batches — the same two shapes, the other species
// ---------------------------------------------------------------------------
// Deliberately NOT generalised into one parameterised "processing batch" entry
// shared with sheep. The two domains agree on the verb and disagree on
// everything a declaration is for: cattle_processing_batches is read by EIGHT
// catalog resources and sheep_processing_batches by five, the overlapping ids
// are different ids, and the cattle table feeds the forecast pair that has no
// sheep counterpart. A shared entry would have to take `tables` and
// `invalidates` as arguments, at which point the "shared" part is the two lines
// of PostgREST and the declaration — the whole point — is still written twice.
//
// The DELETE half has no cattle sibling here on purpose. CattleBatchPage's
// unschedule already goes through unscheduleCattleProcessingBatch (migration
// 100's SECDEF RPC), which is a src/lib owner, not a component write. It is
// therefore not a `.tsx` call site and moving it would be churn, not progress.

/** The columns CattleBatchesView composes for a newly scheduled batch. */
/**
 * Migration 240 (R4): the create transport is the audited
 * `cattle_batch_operation` RPC's batch.create — the row is server-built from
 * (batchId, name, planned_process_date) exactly as the old client literal
 * built it, and the Activity record.created is server-written.
 */
export type CattleBatchCreateVars = {
  batchId: string;
  name: string;
  plannedProcessDate: string;
};

/**
 * Migration 240 (R4): the patch entry's transport is the audited
 * `cattle_batch_operation` RPC (src/lib/cattleOperationsApi.ts owns the
 * request id, the retry and the reply's meaning). One entry still serves
 * CattleBatchPage's rename, its scheduled-date editor and its weight grid —
 * the caller now states WHICH operation and the expected-before values, and
 * this entry's declared invalidation set is unchanged.
 */
export type CattleBatchPatchVars = {
  batchId: string;
  operation: 'batch.update' | 'batch.weight';
  target: Record<string, unknown>;
  expected: Record<string, unknown>;
  change: Record<string, unknown>;
  requestId: string;
};

/**
 * Migration 240 (R4): the create is the audited batch.create operation. The
 * old `.insert(row).select().single()` requested the row back because the
 * caller navigates on it; the operation's reply carries the fresh row the
 * same way (`rows.batch`), so the navigation contract is unchanged. A UNIQUE
 * name refusal propagates as 23505 with the PostgREST code, so the caller's
 * suggested-name re-allocation keeps working unchanged.
 */
async function writeCattleBatchCreate(
  sb: SupabaseClient,
  {batchId, name, plannedProcessDate}: CattleBatchCreateVars,
): Promise<Record<string, unknown> | null> {
  if (!batchId) throw new Error('cattle.batch.create: batchId is required');
  const outcome = await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'batch.create',
    target: {batch_id: batchId},
    expected: {},
    change: {name, planned_process_date: plannedProcessDate},
  });
  const rows = (outcome as {rows?: Record<string, unknown> | null}).rows;
  return (rows && typeof rows === 'object' ? (rows.batch as Record<string, unknown>) : null) ?? null;
}

async function writeCattleBatchPatch(sb: SupabaseClient, vars: CattleBatchPatchVars): Promise<null> {
  if (!vars.batchId) throw new Error('cattle.batch.patch: batchId is required');
  // runCattleOperation resolves on saved/no_change and throws the notice text
  // for conflict/not_attached/refusals — the same shape this entry's callers
  // already render. The RPC writes the Activity row and the receipt in-tx, so
  // the page's client-side logEvent/logStatus for these edits is gone.
  await runCattleOperation(sb, {
    requestId: vars.requestId,
    operation: vars.operation,
    target: {batch_id: vars.batchId, ...vars.target},
    expected: vars.expected,
    change: vars.change,
  });
  return null;
}

// ---------------------------------------------------------------------------
// breeding records — calving and lambing
// ---------------------------------------------------------------------------
// Two entries rather than one parameterised "breeding record" entry, for the
// reason given above the cattle batch pair: the tables differ, the reader sets
// differ (six against two), and a shared entry would take both as arguments.
//
// These are the LAST direct component writes on either table. Their DELETES
// already left the client — migrations 079 and 094 own them as SECDEF RPCs
// (cattleCalvingApi, sheepLambingApi) — so with the inserts declared here,
// neither table has an undeclared write anywhere in a `.tsx` file.
//
// The dam's `record.created` Activity emit stays at the call site on purpose.
// It writes activity_events, which none of the invalidated resources read, and
// it is explicitly best-effort ("never block the save"). Pulling it into a
// catalog entry would make a decorative audit row part of a write's success
// contract.

/** One calving row, composed by the caller exactly as it always was, plus the
 * dam row the operation locks (and scopes the Activity event to). */
export type CattleCalvingCreateVars = {
  record: Record<string, unknown>;
  damCattleId: string;
};

/**
 * MIGRATION 244: one lambing row on a live dam sheep (`lambing.create`). The
 * server locks the dam row, inserts the lambing record, and writes the
 * `sheep.animal` `record.created` Activity row and receipt in the same tx.
 */
export type SheepLambingCreateVars = {
  requestId?: string;
  damSheepId?: string;
  record: Record<string, unknown>;
};

// Migration 245: the calving insert is the audited calving.create operation
// (request-id receipt; the 033/044 triggers co-fire in-transaction; the
// record.created Activity row the call site used to emit best-effort is now
// written in the same transaction, scoped to the dam).
async function writeCattleCalvingCreate(
  sb: SupabaseClient,
  {record, damCattleId}: CattleCalvingCreateVars,
): Promise<null> {
  if (!record || typeof record !== 'object') throw new Error('cattle.calving.create: a record is required');
  if (!damCattleId) throw new Error('cattle.calving.create: damCattleId is required');
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'calving.create',
    target: {dam_cattle_id: damCattleId},
    change: record,
  });
  return null;
}

async function writeSheepLambingCreate(
  sb: SupabaseClient,
  {requestId, damSheepId, record}: SheepLambingCreateVars,
): Promise<null> {
  if (!damSheepId) throw new Error('sheep.lambing.create: damSheepId is required');
  if (!record || typeof record !== 'object') throw new Error('sheep.lambing.create: a record is required');
  await runSheepAnimalOperation(sb, {
    requestId: requestId || newSheepAnimalRequestId('sheep-lambing-'),
    operation: 'lambing.create',
    target: {dam_sheep_id: damSheepId},
    expected: {},
    change: record,
  });
  return null;
}

// ---------------------------------------------------------------------------
// sheep animal creation and inline edits — migration 244 (R-H sheep)
// ---------------------------------------------------------------------------
// Every sheep animal write (flocks index Add-sheep, animal record page inline
// field edits + tag retag, and SheepBulkImport via sheepImportApi.ts) now
// routes through `public.sheep_animal_operation`.
export type SheepAnimalCreateVars = {
  requestId?: string;
  record: Record<string, unknown>;
};

async function writeSheepAnimalCreate(sb: SupabaseClient, {requestId, record}: SheepAnimalCreateVars): Promise<null> {
  if (!record || typeof record !== 'object') throw new Error('sheep.animal.create: a record is required');
  await runSheepAnimalOperation(sb, {
    requestId: requestId || newSheepAnimalRequestId('sheep-create-'),
    operation: 'animal.create',
    target: {},
    expected: {},
    change: record,
  });
  return null;
}

/**
 * MIGRATION 244: the record page's inline field editors pass the values read
 * from the loaded sheep row as `expected` and the edited field(s) as `patch`.
 * An inline tag edit (`{tag}`) dispatches `animal.retag` (`source: 'manual'`)
 * so the server composes `old_tags` under `FOR UPDATE`; any other inline edit
 * dispatches `animal.patch` against the closed 11-column allowlist.
 */
export type SheepAnimalPatchVars = {
  requestId?: string;
  sheepId: string;
  expected?: Record<string, unknown>;
  patch: Record<string, unknown>;
};

async function writeSheepAnimalPatch(
  sb: SupabaseClient,
  {requestId, sheepId, expected, patch}: SheepAnimalPatchVars,
): Promise<null> {
  if (!sheepId) throw new Error('sheep.animal.patch: sheepId is required');
  if (!patch || typeof patch !== 'object') throw new Error('sheep.animal.patch: a patch is required');
  const keys = Object.keys(patch);
  if (keys.length === 1 && keys[0] === 'tag') {
    const expTag = expected && 'tag' in expected ? (expected.tag == null ? null : String(expected.tag)) : null;
    const newTag = patch.tag == null ? null : String(patch.tag);
    await runSheepAnimalOperation(sb, {
      requestId: requestId || newSheepAnimalRequestId('sheep-retag-'),
      operation: 'animal.retag',
      target: {sheep_id: sheepId},
      expected: {tag: expTag},
      change: {tag: newTag, source: 'manual'},
    });
    return null;
  }
  await runSheepAnimalOperation(sb, {
    requestId: requestId || newSheepAnimalRequestId('sheep-patch-'),
    operation: 'animal.patch',
    target: {sheep_id: sheepId},
    expected: expected ?? {},
    change: patch,
  });
  return null;
}

// ---------------------------------------------------------------------------
// cattle origins — the herds index "add an origin" inline control
// ---------------------------------------------------------------------------
// The smallest entry in the file, and it is here for a reason the size hides:
// this write's ONLY refresh was a `loadAll()` fired from inside a `.then()`
// callback and never awaited. `cattle_origins` is read by THREE resources — the
// herds index, the animal record page and the forecast — and a new origin that
// exists in the picker on one surface and not on the other two is precisely the
// "a panel quietly stopped updating" failure the declaration prevents.
//
// The other writer of this table, CattleBulkImport, is NOT converted and is not
// claimed: its insert is inside a per-row commit loop (see the lane report), so
// the anti-revert wave names CattleHerdsView alone, exactly as the `sheep`
// entry above names SheepFlocksView alone.
export type CattleOriginCreateVars = {
  requestId?: string;
  row: Record<string, unknown>;
};

// Migration 248: the herds inline "add an origin" control routes through the
// audited cattle.origin.ensure operation on public.ruminant_option_operation.
// Returns the server-resolved option row (on no_change, the existing option row
// matched by id or normalized label) so the caller never depends on a discarded
// client-minted id or non-canonical casing.
async function writeCattleOriginCreate(
  sb: SupabaseClient,
  {requestId, row}: CattleOriginCreateVars,
): Promise<RuminantOptionRow | null> {
  if (!row || typeof row !== 'object') throw new Error('cattle.origin.create: a row is required');
  const outcome = await runRuminantOptionOperation(sb, {
    requestId: requestId || newRuminantOptionRequestId('cattle-origin-'),
    operation: 'cattle.origin.ensure',
    change: row,
  });
  return outcome.option;
}

// ---------------------------------------------------------------------------
// cattle animals — the widest reader set in the file, and the reason for it
// ---------------------------------------------------------------------------
// `cattle` is read by TWELVE catalog resources: the herds index, the animal
// record page, the cattle home tile, the batch index and one batch detail, the
// breeding page, BOTH forecast resources, the animal-history view, the cattle
// log preview, and the two HOME alert/missed-input resources. The three call
// sites retired for this entry refreshed, between them, exactly two of those
// twelve — each surface's own list — because that is all a remembered
// `loadAll()` can reach.
//
// TWO ENTRIES, NOT ONE PER SURFACE. The herds index, the animal record page and
// the forecast's heifer-include modal issue the IDENTICAL update against one
// row, differing only in which columns the operator touched; the herds index
// additionally inserts. So the split here is by VERB, exactly as it is for the
// two processing-batch domains above, and `surfaces` lists the three writers
// rather than the entries being triplicated.
//
// WHAT THIS DOES *NOT* CONVERT, STATED SO THE WAVE BELOW CANNOT OVER-CLAIM
//
// `cattle` keeps three other direct writers, and none of them is this lane's:
// CattleBulkImport's per-row commit loop (a 500-row import must not become 500
// invalidation cycles — the same exclusion SheepBulkImport already carries),
// src/lib/cattleProcessingBatch.ts (a lib module, already a named write seam,
// not a component call site), and src/webforms/WeighInsWebform.tsx (owned by a
// different lane this wave). The anti-revert wave therefore names the three
// modules converted here and NOT the table wholesale — the same (module ×
// table) shape the `sheep` and `cattle_origins` entries record above.
//
// THE ACTIVITY EMIT STAYS AT THE CALL SITE, and for the reason already written
// down for calving: `recordFieldChange` writes activity_events, which none of
// the twelve invalidated resources read, and it is best-effort by contract
// (`runMutation`'s `activityBestEffort` default). Folding it in here would make
// an audit row part of a save's success condition.

/** One cattle row, composed by the herds index exactly as it always was. */
export type CattleAnimalCreateVars = {
  row: Record<string, unknown>;
};

/**
 * Open for the same reason every other patch in this file is: the three writers
 * hand over whichever columns the operator touched — the herds form's twenty,
 * the record page's inline field editors, the forecast modal's heifer-include
 * flag — as one object, against one row.
 */
export type CattleAnimalPatchVars = {
  cattleId: string;
  patch: Record<string, unknown>;
  /** The before-value of every patched field, as the caller read it — the
   * operation compare-and-sets against these and refuses a stale value. */
  expected: Record<string, unknown>;
};

// Migration 245: the herds create is the audited animal.create operation; the
// record.created Activity row the call site emitted best-effort moves into the
// same transaction (C1 wording, source 'herds').
async function writeCattleAnimalCreate(sb: SupabaseClient, {row}: CattleAnimalCreateVars): Promise<null> {
  if (!row || typeof row !== 'object') throw new Error('cattle.animal.create: a row is required');
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'animal.create',
    target: {source: 'herds'},
    change: row,
  });
  return null;
}

// Migration 245: the inline-editor patch is the audited animal.patch
// operation — per-field compare-and-set on `expected`, one field.updated
// Activity row in-transaction. `tag` is refused here on purpose: tag edits
// move through the animal.retag operation at the call site (the server
// composes old_tags there).
async function writeCattleAnimalPatch(
  sb: SupabaseClient,
  {cattleId, patch, expected}: CattleAnimalPatchVars,
): Promise<null> {
  if (!cattleId) throw new Error('cattle.animal.patch: cattleId is required');
  if (!patch || typeof patch !== 'object') throw new Error('cattle.animal.patch: a patch is required');
  if (!expected || typeof expected !== 'object') throw new Error('cattle.animal.patch: expected is required');
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'animal.patch',
    target: {cattle_id: cattleId},
    expected,
    change: patch,
  });
  return null;
}

// ---------------------------------------------------------------------------
// equipment maintenance events — the first IDEMPOTENT write in the catalog
// ---------------------------------------------------------------------------
// PR #218 could not convert this pair and said so: EquipmentMaintenanceModal
// treats SQLSTATE 23505 on `client_submission_id` as SUCCESS, and the seam's
// rejection discarded `error.code`. That is fixed above — in the seam, not
// here — and the two writes follow unchanged.
//
// WHAT THE ENTRIES DELIBERATELY DO NOT DO
//
// Neither entry inspects the code itself. Swallowing 23505 inside `run` would
// look tidier and would be wrong twice over: it would make an idempotency
// policy that belongs to ONE form (this modal mints one submission id per modal
// instance, so a replay is a genuine duplicate) into a property of the table,
// and it would report success from a write that never happened for any other
// caller that later reuses the entry. The catalog adapts to the write it wraps
// — the same rule `sheep.batch.delete` records for its envelope — and the
// surface keeps the branch it already had.
//
// THE ONE CONSEQUENCE, STATED RATHER THAN LEFT TO BE FOUND
//
// Because `run` rejects on 23505, `onSuccess` does not fire and the declared
// invalidation does NOT run on the tolerated-duplicate path. That path is
// reached only when an earlier in-flight submit of the SAME row already
// succeeded — and that submit went through this same entry and invalidated on
// its own success. The modal additionally calls `onSaved()` on both paths, so
// EquipmentDetail's `onReload` still runs exactly as before. Nothing that
// refreshed before refreshes less.

/** One maintenance event row, composed by the modal exactly as it always was. */
export type EquipmentMaintenanceCreateVars = {
  row: Record<string, unknown>;
};

/**
 * Open for the same reason the batch patches are: the modal's single `save()`
 * hands over whichever of the seven editable columns the operator touched, as
 * one object, on one row.
 */
export type EquipmentMaintenancePatchVars = {
  eventId: string;
  patch: Record<string, unknown>;
};

async function writeEquipmentMaintenanceCreate(
  sb: SupabaseClient,
  {row}: EquipmentMaintenanceCreateVars,
): Promise<null> {
  if (!row || typeof row !== 'object') throw new Error('equipment.maintenance.create: a row is required');
  return unwrapWrite<null>(await sb.from('equipment_maintenance_events').insert(row));
}

/**
 * The patch READS BACK the row it claims to have written, and that is load
 * bearing (migration 225 lane).
 *
 * A bare `.update(patch).eq('id', eventId)` reports only transport and
 * constraint errors: when a policy hides the row, PostgREST updates ZERO rows
 * and still answers 204, so the write resolves as success. The modal then
 * deleted the Storage objects the operator had removed — destroying files on
 * the strength of a save that never happened.
 *
 * `.select('id').single()` sends `Prefer: return=representation`, so PostgREST
 * fails the request (PGRST116) unless exactly one row came back. The caller
 * therefore cannot mistake a refusal for a save. The returned row is the
 * confirmation itself, not decoration.
 *
 * The CREATE leg deliberately keeps its bare `.insert(row)`: an insert cannot
 * report success having written nothing (a policy refusal raises 42501), and
 * its shape is what the modal's 23505 duplicate-submission branch reads.
 */
async function writeEquipmentMaintenancePatch(
  sb: SupabaseClient,
  {eventId, patch}: EquipmentMaintenancePatchVars,
): Promise<Record<string, unknown> | null> {
  if (!eventId) throw new Error('equipment.maintenance.patch: eventId is required');
  if (!patch || typeof patch !== 'object') throw new Error('equipment.maintenance.patch: a patch is required');
  return unwrapWrite<Record<string, unknown>>(
    await sb.from('equipment_maintenance_events').update(patch).eq('id', eventId).select('id').single(),
  );
}

// ---------------------------------------------------------------------------
// equipment material clears — the standard home's Materials Needed card
// ---------------------------------------------------------------------------
// `equipment_material_clears` has exactly ONE reader today, and the card's own
// `retryMaterials()` already refreshes it. So this entry buys no invalidation
// the surface was missing — and it is still worth declaring, for the reason the
// header gives about `home.standard.production.sources`: the required set is
// COMPUTED from the read catalog, so the day a second resource reads a clear
// row, this write is told. A remembered refetch would not be.
//
// The write is idempotent in the same shape as the maintenance insert (a unique
// key on the material/bucket pair, tolerated at the call site), which is the
// second reason it belongs in the same wave as the seam fix.
export type EquipmentMaterialClearCreateVars = {
  row: Record<string, unknown>;
};

async function writeEquipmentMaterialClearCreate(
  sb: SupabaseClient,
  {row}: EquipmentMaterialClearCreateVars,
): Promise<null> {
  if (!row || typeof row !== 'object') throw new Error('equipment.materialClear.create: a row is required');
  return unwrapWrite<null>(await sb.from('equipment_material_clears').insert(row));
}

// ---------------------------------------------------------------------------
// Projects jobs (migration 256): one operation RPC over private tables
// ---------------------------------------------------------------------------
// Every Projects write is ONE call to project_job_operation carrying the exact
// request the page built (request id, operation, target, expected, change).
// projectJobsApi owns the RPC and its transport-only retry with the same id;
// this entry adds the declaration and nothing else. The Projects tables have no
// client grant (the page reads them through list_project_jobs), so no read
// resource names them and no client module writes them: the static guard
// admits them only from the committed 256 SQL.
//
// A refused or unconfirmed outcome REJECTS (runProjectJobOperation throws a
// ProjectJobOperationError), so the runtime's declared invalidations run on a
// saved write only. The page then reconciles a refusal over these same
// declared keys, in a way that cannot mask the operation's own outcome.

export type ProjectJobOperationVars = ProjectJobOpRequest;

async function writeProjectJobOperation(sb: SupabaseClient, request: ProjectJobOperationVars) {
  return runProjectJobOperation(sb, request);
}

// ---------------------------------------------------------------------------
// Daily Record Edits (Migration 259)
// ---------------------------------------------------------------------------
export type DailyReportEditVars = DailyEditFenceInput;

async function writeDailyReportEdit(sb: SupabaseClient, vars: DailyReportEditVars): Promise<DailyEditOutcome> {
  const {updateDailyReportFenced} = await import('../../lib/dailyReportEditOperationApi.js');
  return updateDailyReportFenced(sb, vars);
}

// ---------------------------------------------------------------------------
// The entry shape
// ---------------------------------------------------------------------------

/**
 * One declared write.
 *
 * `surfaces`, `tables` and `rpcs` mirror CatalogResourceBase deliberately — the
 * read catalog already joins those three against the frozen physical call-site
 * inventory, and a write catalog that spelled them differently would need a
 * second, differently-shaped join for no gain.
 *
 * `invalidates` is the member that has no read-side sibling, and it is the
 * whole point of the file. It names RESOURCE IDS, and the rule it must satisfy
 * is derivable rather than judged:
 *
 *   EVERY catalog resource that reads a table this mutation writes must appear
 *   here.
 *
 * Both halves of that come from data already in the tree — `tables` from this
 * entry, the reader set from routeDataCatalog — so the guard computes the
 * required set and diffs it. Under-declaring is the defect (a surface serves
 * rows from before the write). Over-declaring is not a defect and is not
 * rejected: invalidation only marks an entry stale, and only an ACTIVE observer
 * refetches, so naming a resource nobody is looking at costs one map lookup.
 *
 * `owners` names the SOURCE STEMS that physically issue this write — extension
 * omitted on purpose, because `src/App.jsx` became `src/App.tsx` and every
 * guard that had named an extension went quietly green over a file it could no
 * longer find. The guard resolves each stem through scripts/lib/sourceStem.cjs,
 * which throws on absence and on ambiguity rather than guessing, and then reads
 * the real write verbs out of the AST. That is what stops `tables` from being a
 * claim: a table nobody in `owners` writes, and nobody in the read catalog
 * reads, fails by name.
 *
 * `run` is pure in the same sense the read catalog's `query` is: no React, no
 * context, no navigation. It takes a client and its variables and returns or
 * throws. It deliberately takes NO AbortSignal — a read may be abandoned when
 * its observer goes away, but a write that has been sent must not be, and
 * offering a signal here would invite exactly that.
 */
export type RouteMutation<Vars, Data> = {
  readonly id: string;
  readonly kind: 'write';
  readonly surfaces: readonly string[];
  readonly tables: readonly string[];
  readonly rpcs?: readonly string[];
  readonly owners: readonly string[];
  readonly invalidates: readonly string[];
  readonly run: (sb: SupabaseClient, vars: Vars) => Promise<Data>;
};

/**
 * Payloads are `unknown` at the catalog level for the same reason the read
 * catalog's are: this is keyed by a runtime string, so nothing at the boundary
 * knows which entry was asked for, and a union here would let a caller read the
 * wrong arm without narrowing. Callers pin their own shape through
 * useRouteMutation's type parameters.
 */
export type AnyRouteMutation = RouteMutation<never, unknown>;

const MUTATION_ENTRIES: Readonly<Record<string, AnyRouteMutation>> = Object.freeze({
  [MUTATION.SHEEP_BATCH_CREATE]: Object.freeze({
    id: MUTATION.SHEEP_BATCH_CREATE,
    kind: 'write',
    surfaces: Object.freeze(['sheep.batches.index']),
    tables: Object.freeze(['sheep_processing_batches']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // Every reader of sheep_processing_batches. Only `sheep.batches.core` is
    // mounted at the call site; the rest are the ones a remembered `loadAll()`
    // has never named and never could.
    //
    // `home.standard.production.sources` is not here because anyone thought of
    // it. It landed on main while this lane was open, and the guard derived the
    // requirement from its `tables` and failed by name until it was added. That
    // is the property the whole file exists for: the set is computed from the
    // read catalog, so a new reader cannot appear without the writes that
    // affect it being told.
    invalidates: Object.freeze([
      RESOURCE.SHEEP_BATCHES_CORE,
      RESOURCE.SHEEP_BATCH_DETAIL,
      RESOURCE.SHEEP_HOME_CORE,
      RESOURCE.PRODUCTION_SOURCES_CORE,
      RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
    ]),
    run: writeSheepBatchCreate,
  }),
  [MUTATION.SHEEP_BATCH_PATCH]: Object.freeze({
    id: MUTATION.SHEEP_BATCH_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['sheep.batches.detail']),
    tables: Object.freeze(['sheep_processing_batches']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    invalidates: Object.freeze([
      RESOURCE.SHEEP_BATCHES_CORE,
      RESOURCE.SHEEP_BATCH_DETAIL,
      RESOURCE.SHEEP_HOME_CORE,
      RESOURCE.PRODUCTION_SOURCES_CORE,
      RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
    ]),
    run: writeSheepBatchPatch,
  }),
  [MUTATION.SHEEP_BATCH_DELETE]: Object.freeze({
    id: MUTATION.SHEEP_BATCH_DELETE,
    kind: 'write',
    surfaces: Object.freeze(['sheep.batches.detail']),
    // TWO tables, and the second one is the reason the declaration is worth
    // writing down. Migration 100's RPC does not only delete the batch row: it
    // "clears straggler sheep.processing_batch_id links" first, in the same
    // transaction. A reviewer reading only the call site sees a batch delete; a
    // reviewer reading this sees that the sheep directory changed too, and the
    // required-invalidation set below doubles because of it.
    tables: Object.freeze(['sheep_processing_batches', 'sheep']),
    rpcs: Object.freeze(['delete_sheep_processing_batch']),
    // The write leaves this file entirely: processingBatchDeleteApi is the RPC's
    // client owner and stays so. `owners` is what keeps that delegation
    // checkable — the guard reads THAT module and requires the RPC name to be
    // called there.
    owners: Object.freeze(['src/lib/processingBatchDeleteApi']),
    invalidates: Object.freeze([
      RESOURCE.SHEEP_BATCHES_CORE,
      RESOURCE.SHEEP_BATCH_DETAIL,
      RESOURCE.SHEEP_HOME_CORE,
      RESOURCE.PRODUCTION_SOURCES_CORE,
      RESOURCE.SHEEP_FLOCKS_CORE,
      RESOURCE.SHEEP_ANIMAL_DETAIL,
      RESOURCE.ANIMAL_HISTORY_CORE,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      // The straggler-clearing half of this RPC is what reaches the weigh-in
      // record page: weighin.session.detail reads the sheep directory a sheep
      // session resolves its tags and reconcile candidates against, and the
      // public weigh-in FORM reads the same directory for its tag picker.
      RESOURCE.WEIGHIN_SESSION_DETAIL,
      RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY,
      // And the Pasture Map: its planner roster counts flock groups off the
      // same sheep directory, so a batch delete that clears straggler links can
      // change which flock groups exist and how many head each carries.
      RESOURCE.PASTURE_PLANNER_ROSTER,
    ]),
    run: writeSheepBatchDelete,
  }),
  [MUTATION.CATTLE_BATCH_CREATE]: Object.freeze({
    id: MUTATION.CATTLE_BATCH_CREATE,
    kind: 'write',
    surfaces: Object.freeze(['cattle.batches.index']),
    tables: Object.freeze(['cattle_processing_batches']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // EIGHT readers, and the count is the argument for declaring this at all.
    // scheduleVirtualBatch called NO refetch — it navigated to the new record
    // page instead — so on the way out it left the batch index, the cattle home
    // tile, both production summaries, the animal record page and BOTH forecast
    // resources holding rows from before the batch existed. Only the index and
    // the forecast pair were even plausibly mounted, which is exactly the point:
    // nobody was in a position to enumerate the rest, and nothing checked.
    invalidates: Object.freeze([
      RESOURCE.CATTLE_BATCHES_CORE,
      RESOURCE.CATTLE_BATCH_DETAIL,
      RESOURCE.CATTLE_HOME_CORE,
      RESOURCE.CATTLE_ANIMAL_DETAIL,
      RESOURCE.CATTLE_FORECAST_BUNDLE,
      RESOURCE.CATTLE_FORECAST_CORE,
      RESOURCE.PRODUCTION_SOURCES_CORE,
      RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
    ]),
    run: writeCattleBatchCreate,
  }),
  [MUTATION.CATTLE_BATCH_PATCH]: Object.freeze({
    id: MUTATION.CATTLE_BATCH_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['cattle.batches.detail']),
    tables: Object.freeze(['cattle_processing_batches']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    invalidates: Object.freeze([
      RESOURCE.CATTLE_BATCHES_CORE,
      RESOURCE.CATTLE_BATCH_DETAIL,
      RESOURCE.CATTLE_HOME_CORE,
      RESOURCE.CATTLE_ANIMAL_DETAIL,
      RESOURCE.CATTLE_FORECAST_BUNDLE,
      RESOURCE.CATTLE_FORECAST_CORE,
      RESOURCE.PRODUCTION_SOURCES_CORE,
      RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
    ]),
    run: writeCattleBatchPatch,
  }),
  [MUTATION.CATTLE_CALVING_CREATE]: Object.freeze({
    id: MUTATION.CATTLE_CALVING_CREATE,
    kind: 'write',
    // TWO surfaces, because two of them issue the identical insert: the herds
    // index (CattleHerdsView's inline calving form) and the animal record page.
    surfaces: Object.freeze(['cattle.herds.index', 'cattle.herds.detail']),
    tables: Object.freeze(['cattle_calving_records']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // Six readers. Both retired call sites refreshed exactly one of them — the
    // resource the writing surface happened to be mounted on — so the breeding
    // page, the cattle home tile, the batch index and the forecast carried
    // pre-write calving counts for any observer that stayed mounted.
    invalidates: Object.freeze([
      RESOURCE.CATTLE_HERDS_CORE,
      RESOURCE.CATTLE_ANIMAL_DETAIL,
      RESOURCE.CATTLE_HOME_CORE,
      RESOURCE.CATTLE_BATCHES_CORE,
      RESOURCE.CATTLE_BREEDING_CORE,
      RESOURCE.CATTLE_FORECAST_CORE,
    ]),
    run: writeCattleCalvingCreate,
  }),
  [MUTATION.SHEEP_LAMBING_CREATE]: Object.freeze({
    id: MUTATION.SHEEP_LAMBING_CREATE,
    kind: 'write',
    // ONE surface, and the asymmetry with cattle above is real rather than an
    // omission: SheepFlocksView has no inline lambing form, so the record page
    // is the only writer.
    surfaces: Object.freeze(['sheep.flocks.detail']),
    tables: Object.freeze(['sheep_lambing_records']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    invalidates: Object.freeze([RESOURCE.SHEEP_FLOCKS_CORE, RESOURCE.SHEEP_ANIMAL_DETAIL]),
    run: writeSheepLambingCreate,
  }),
  [MUTATION.SHEEP_ANIMAL_CREATE]: Object.freeze({
    id: MUTATION.SHEEP_ANIMAL_CREATE,
    kind: 'write',
    surfaces: Object.freeze(['sheep.flocks.index']),
    tables: Object.freeze(['sheep']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // TEN readers, including two HOME resources — COUNTED off the array below
    // on the merged tree, not carried over from either branch. Both branches
    // arrived here with the prose disagreeing with the list: main said EIGHT
    // and listed nine, the sheep lane corrected the sentence to nine, and the
    // pasture-map lane appended a tenth id while leaving the leading word
    // alone. Neither was wrong on its own branch and only the merged tree
    // shows both, which is the same failure `cattle.animal.patch` records
    // below for the identical id.
    //
    // A new sheep changes the standard home's alert inputs and the light home's
    // missed-inputs tile, and the retired call site's `loadAll()` refreshed
    // neither — it refreshed the flocks index it was standing on. The eighth
    // and ninth are the weigh-in record page and the weigh-in webform's species
    // directory, which arrived when view.weighinsessions localized; the tenth
    // is the Pasture Map's planner roster, which counts flock groups off that
    // same directory and arrived when view.pastureMap localized.
    invalidates: Object.freeze([
      RESOURCE.SHEEP_HOME_CORE,
      RESOURCE.SHEEP_FLOCKS_CORE,
      RESOURCE.SHEEP_ANIMAL_DETAIL,
      RESOURCE.SHEEP_BATCH_DETAIL,
      RESOURCE.ANIMAL_HISTORY_CORE,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.WEIGHIN_SESSION_DETAIL,
      RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY,
      RESOURCE.PASTURE_PLANNER_ROSTER,
    ]),
    run: writeSheepAnimalCreate,
  }),
  [MUTATION.SHEEP_ANIMAL_PATCH]: Object.freeze({
    id: MUTATION.SHEEP_ANIMAL_PATCH,
    kind: 'write',
    // ONE surface, and the asymmetry with cattle is real rather than an
    // omission: cattle's identical update is issued from three places (the
    // herds index, the record page and the forecast's heifer modal), while the
    // sheep record page is the only surface with inline field editors.
    surfaces: Object.freeze(['sheep.flocks.detail']),
    tables: Object.freeze(['sheep']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // The same TEN readers `sheep.animal.create` names, because the two writes
    // touch the same row on the same table — and naming them here is the whole
    // point of the conversion. The retired call site refreshed exactly ONE of
    // the ten: its own `loadAll()`, i.e. `sheep.animal.detail`. So renaming a
    // ewe, correcting a birth date or flipping a status on this page left the
    // flocks index, the sheep home, the batch record page, animal history, both
    // HOME resources, both weigh-in surfaces and the pasture planner roster
    // holding the previous value for any observer that stayed mounted. That is the identical defect
    // `cattle.animal.patch` closed for cattle, one species later.
    invalidates: Object.freeze([
      RESOURCE.SHEEP_HOME_CORE,
      RESOURCE.SHEEP_FLOCKS_CORE,
      RESOURCE.SHEEP_ANIMAL_DETAIL,
      RESOURCE.SHEEP_BATCH_DETAIL,
      RESOURCE.ANIMAL_HISTORY_CORE,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.WEIGHIN_SESSION_DETAIL,
      RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY,
      // ADDED AT INTEGRATION, and this id is exactly why the count above is
      // re-derived rather than transcribed. The sheep lane derived its reader
      // set against a tree where the Pasture Map had not localized yet, so it
      // named nine; the pasture-map lane added PASTURE_PLANNER_ROSTER to
      // `sheep.animal.create` on a different branch. The two merge cleanly and
      // WRONGLY — nothing conflicts, and the result is a `sheep` write that
      // leaves the planner roster showing the previous flock counts. The same
      // gap, on the same resource, is recorded on `cattle.animal.patch` below.
      RESOURCE.PASTURE_PLANNER_ROSTER,
    ]),
    run: writeSheepAnimalPatch,
  }),
  [MUTATION.CATTLE_ORIGIN_CREATE]: Object.freeze({
    id: MUTATION.CATTLE_ORIGIN_CREATE,
    kind: 'write',
    surfaces: Object.freeze(['cattle.herds.index']),
    tables: Object.freeze(['cattle_origins']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // Three readers; the retired call site refreshed one of them, from inside a
    // `.then()` and without awaiting it.
    invalidates: Object.freeze([
      RESOURCE.CATTLE_HERDS_CORE,
      RESOURCE.CATTLE_ANIMAL_DETAIL,
      RESOURCE.CATTLE_FORECAST_CORE,
    ]),
    run: writeCattleOriginCreate,
  }),
  [MUTATION.CATTLE_ANIMAL_CREATE]: Object.freeze({
    id: MUTATION.CATTLE_ANIMAL_CREATE,
    kind: 'write',
    // ONE surface: only the herds index has an "add cow" form. The record page
    // and the forecast modal edit an animal that already exists.
    surfaces: Object.freeze(['cattle.herds.index']),
    tables: Object.freeze(['cattle']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    invalidates: Object.freeze([
      RESOURCE.CATTLE_HERDS_CORE,
      RESOURCE.CATTLE_ANIMAL_DETAIL,
      RESOURCE.CATTLE_HOME_CORE,
      RESOURCE.CATTLE_BATCHES_CORE,
      RESOURCE.CATTLE_BATCH_DETAIL,
      RESOURCE.CATTLE_BREEDING_CORE,
      RESOURCE.CATTLE_FORECAST_BUNDLE,
      RESOURCE.CATTLE_FORECAST_CORE,
      RESOURCE.CATTLE_LOG_CATTLE_PREVIEW,
      RESOURCE.ANIMAL_HISTORY_CORE,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      // Added when the weigh-in surfaces localized in the same wave: both read
      // `cattle`, so a cattle write that did not invalidate them would leave the
      // session page and the webform's species list showing an animal that no
      // longer exists — or missing one that now does — until the next remount.
      // The arithmetic guard found these; review did not.
      RESOURCE.WEIGHIN_SESSION_DETAIL,
      RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY,
      // The pasture planner roster reads `cattle` too, and it arrived on a
      // DIFFERENT branch than these entries — so neither diff was wrong alone
      // and only the merged tree shows the gap. Without this, adding or editing
      // a cattle animal leaves the pasture planner showing the previous roster.
      RESOURCE.PASTURE_PLANNER_ROSTER,
    ]),
    run: writeCattleAnimalCreate,
  }),
  [MUTATION.CATTLE_ANIMAL_PATCH]: Object.freeze({
    id: MUTATION.CATTLE_ANIMAL_PATCH,
    kind: 'write',
    // THREE surfaces, because three of them issue the identical update: the
    // herds index (its cow form and its inline field patcher), the animal
    // record page, and the forecast's heifer-include modal.
    surfaces: Object.freeze(['cattle.herds.index', 'cattle.herds.detail', 'view.cattleforecast']),
    tables: Object.freeze(['cattle']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // Same twelve. Worth naming what the retired sites actually refreshed: the
    // herds index refetched its own list, the record page refetched its own
    // record, and the forecast modal called a `reload` prop scoped to the
    // forecast. A herd change made on the record page therefore left the
    // breeding page, the batch index, the cattle log preview and both HOME
    // resources holding the previous herd for any observer that stayed mounted.
    invalidates: Object.freeze([
      RESOURCE.CATTLE_HERDS_CORE,
      RESOURCE.CATTLE_ANIMAL_DETAIL,
      RESOURCE.CATTLE_HOME_CORE,
      RESOURCE.CATTLE_BATCHES_CORE,
      RESOURCE.CATTLE_BATCH_DETAIL,
      RESOURCE.CATTLE_BREEDING_CORE,
      RESOURCE.CATTLE_FORECAST_BUNDLE,
      RESOURCE.CATTLE_FORECAST_CORE,
      RESOURCE.CATTLE_LOG_CATTLE_PREVIEW,
      RESOURCE.ANIMAL_HISTORY_CORE,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      // Added when the weigh-in surfaces localized in the same wave: both read
      // `cattle`, so a cattle write that did not invalidate them would leave the
      // session page and the webform's species list showing an animal that no
      // longer exists — or missing one that now does — until the next remount.
      // The arithmetic guard found these; review did not.
      RESOURCE.WEIGHIN_SESSION_DETAIL,
      RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY,
      // The pasture planner roster reads `cattle` too, and it arrived on a
      // DIFFERENT branch than these entries — so neither diff was wrong alone
      // and only the merged tree shows the gap. Without this, adding or editing
      // a cattle animal leaves the pasture planner showing the previous roster.
      RESOURCE.PASTURE_PLANNER_ROSTER,
    ]),
    run: writeCattleAnimalPatch,
  }),
  [MUTATION.EQUIPMENT_MAINTENANCE_CREATE]: Object.freeze({
    id: MUTATION.EQUIPMENT_MAINTENANCE_CREATE,
    kind: 'write',
    // The modal is only ever rendered by EquipmentDetail, which EquipmentHome
    // renders under the resource that serves this surface. One surface, not the
    // four EQUIPMENT_FLEET_CORE serves: the other three are the fueling and
    // checklist entry points, which never open this modal.
    surfaces: Object.freeze(['equipment.fleet.detail']),
    tables: Object.freeze(['equipment_maintenance_events']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    // One reader, and it is the resource the modal is standing on. The
    // conversion's value here is not a resource the old code missed — it is
    // that the write now HAS a checked contract, so a second reader cannot
    // appear without this entry being told.
    invalidates: Object.freeze([RESOURCE.EQUIPMENT_FLEET_CORE]),
    run: writeEquipmentMaintenanceCreate,
  }),
  [MUTATION.EQUIPMENT_MAINTENANCE_PATCH]: Object.freeze({
    id: MUTATION.EQUIPMENT_MAINTENANCE_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['equipment.fleet.detail']),
    tables: Object.freeze(['equipment_maintenance_events']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    invalidates: Object.freeze([RESOURCE.EQUIPMENT_FLEET_CORE]),
    run: writeEquipmentMaintenancePatch,
  }),
  [MUTATION.EQUIPMENT_MATERIAL_CLEAR_CREATE]: Object.freeze({
    id: MUTATION.EQUIPMENT_MATERIAL_CLEAR_CREATE,
    kind: 'write',
    // The standard home, not an equipment surface: after the 2026-05-14
    // retirement of /fleet/materials this card is the only place a clear is
    // issued from, and HOME_STANDARD_EQUIPMENT_MATERIALS is what serves it.
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['equipment_material_clears']),
    owners: Object.freeze(['src/app/data/routeMutationCatalog']),
    invalidates: Object.freeze([RESOURCE.HOME_STANDARD_EQUIPMENT_MATERIALS]),
    run: writeEquipmentMaterialClearCreate,
  }),
  [MUTATION.PROJECT_JOB_OPERATION]: Object.freeze({
    id: MUTATION.PROJECT_JOB_OPERATION,
    kind: 'write',
    // The Projects page, plus the To Do list and item page, where a manager's
    // Send To Projects creates a job from an open To Do (D11).
    surfaces: Object.freeze(['view.projects', 'tasks.center', 'tasks.todo.detail']),
    // The domain tables project_job_operation writes in migration 256 (job,
    // people, photos, and a sent To Do's status). Its receipts and Activity rows
    // follow this catalog's domain-table convention and are not declared. Each
    // declared table is proved against the committed SQL by the static guard.
    tables: Object.freeze(['project_jobs', 'project_job_assignees', 'project_job_photos', 'todo_items']),
    rpcs: Object.freeze(['project_job_operation']),
    owners: Object.freeze(['src/lib/projectJobsApi']),
    // Both readers are RPC-only (`tables: []`), so table-to-reader inference
    // can find neither and they are declared by hand. The To Do list is
    // invalidated on every operation, a safe over-invalidation: only Send To
    // Projects changes it.
    invalidates: Object.freeze([RESOURCE.PROJECT_JOBS, RESOURCE.TASKS_TODO_LIST]),
    run: writeProjectJobOperation,
  }),
  [MUTATION.POULTRY_DAILY_PATCH]: Object.freeze({
    id: MUTATION.POULTRY_DAILY_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['broiler.dailys.detail']),
    tables: Object.freeze(['poultry_dailys']),
    rpcs: Object.freeze(['update_daily_report_fenced']),
    owners: Object.freeze(['src/lib/dailyReportEditOperationApi']),
    invalidates: Object.freeze([
      RESOURCE.HOME_STANDARD_ANIMAL_DAILY_HISTORY,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.BROILER_DAILYS_LIST,
      RESOURCE.BROILER_DAILY_DETAIL,
      RESOURCE.BROILER_HOME_CORE,
      RESOURCE.BROILER_BATCHES_HUB,
      RESOURCE.BROILER_BATCH_FORM_DAILYS,
      RESOURCE.ANIMAL_HISTORY_DAILYS,
      RESOURCE.BROILER_FEED_CORE,
    ]),
    run: writeDailyReportEdit,
  }),
  [MUTATION.LAYER_DAILY_PATCH]: Object.freeze({
    id: MUTATION.LAYER_DAILY_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['layer.dailys.detail']),
    tables: Object.freeze(['layer_dailys']),
    rpcs: Object.freeze(['update_daily_report_fenced']),
    owners: Object.freeze(['src/lib/dailyReportEditOperationApi']),
    invalidates: Object.freeze([
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_STANDARD_ANIMAL_LAYER_EVIDENCE,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.LAYER_BATCHES_CORE,
      RESOURCE.LAYER_GROUPS_CORE,
      RESOURCE.LAYER_DAILYS_LIST,
      RESOURCE.LAYER_DAILY_DETAIL,
      RESOURCE.LAYERS_HOME_CORE,
      RESOURCE.LAYER_RECORD_CORE,
      RESOURCE.ANIMAL_HISTORY_DAILYS,
      RESOURCE.BROILER_FEED_CORE,
    ]),
    run: writeDailyReportEdit,
  }),
  [MUTATION.EGG_DAILY_PATCH]: Object.freeze({
    id: MUTATION.EGG_DAILY_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['layer.eggDailys.detail']),
    tables: Object.freeze(['egg_dailys']),
    rpcs: Object.freeze(['update_daily_report_fenced']),
    owners: Object.freeze(['src/lib/dailyReportEditOperationApi']),
    invalidates: Object.freeze([
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.LAYER_BATCHES_CORE,
      RESOURCE.EGG_DAILYS_LIST,
      RESOURCE.EGG_DAILY_DETAIL,
      RESOURCE.LAYERS_HOME_CORE,
      RESOURCE.LAYER_RECORD_CORE,
      RESOURCE.PRODUCTION_SOURCES_CORE,
      RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
    ]),
    run: writeDailyReportEdit,
  }),
  [MUTATION.PIG_DAILY_PATCH]: Object.freeze({
    id: MUTATION.PIG_DAILY_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['pig.dailys.detail']),
    tables: Object.freeze(['pig_dailys']),
    rpcs: Object.freeze(['update_daily_report_fenced']),
    owners: Object.freeze(['src/lib/dailyReportEditOperationApi']),
    invalidates: Object.freeze([
      RESOURCE.HOME_STANDARD_ANIMAL_DAILY_HISTORY,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.PIG_DAILYS_LIST,
      RESOURCE.PIG_DAILY_DETAIL,
      RESOURCE.ANIMAL_HISTORY_DAILYS,
      RESOURCE.PIG_HOME_CORE,
      RESOURCE.PIG_SOWS_CORE,
      RESOURCE.PIG_FEED_CORE,
      RESOURCE.PIG_BATCHES_CORE,
    ]),
    run: writeDailyReportEdit,
  }),
  [MUTATION.CATTLE_DAILY_PATCH]: Object.freeze({
    id: MUTATION.CATTLE_DAILY_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['cattle.dailys.detail']),
    tables: Object.freeze(['cattle_dailys']),
    rpcs: Object.freeze(['update_daily_report_fenced']),
    owners: Object.freeze(['src/lib/dailyReportEditOperationApi']),
    invalidates: Object.freeze([
      RESOURCE.CATTLE_HOME_CORE,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.CATTLE_DAILY_DETAIL,
      RESOURCE.CATTLE_DAILYS_LIST,
    ]),
    run: writeDailyReportEdit,
  }),
  [MUTATION.SHEEP_DAILY_PATCH]: Object.freeze({
    id: MUTATION.SHEEP_DAILY_PATCH,
    kind: 'write',
    surfaces: Object.freeze(['sheep.dailys.detail']),
    tables: Object.freeze(['sheep_dailys']),
    rpcs: Object.freeze(['update_daily_report_fenced']),
    owners: Object.freeze(['src/lib/dailyReportEditOperationApi']),
    invalidates: Object.freeze([
      RESOURCE.SHEEP_HOME_CORE,
      RESOURCE.HOME_STANDARD_ALERT_INPUTS,
      RESOURCE.HOME_LIGHT_MISSED_INPUTS,
      RESOURCE.SHEEP_DAILY_DETAIL,
      RESOURCE.SHEEP_DAILYS_LIST,
    ]),
    run: writeDailyReportEdit,
  }),
});

export const MUTATION_CATALOG = MUTATION_ENTRIES;

/**
 * Resolve an entry, or throw naming the id that was asked for.
 *
 * Same fail-closed posture as getResource: an unknown mutation id is a bug in
 * the caller, and returning undefined would let a write silently become a
 * no-op — which on a write path means the operator's edit is simply lost with
 * no error anywhere.
 */
export function getMutation(id: string): AnyRouteMutation {
  const entry = MUTATION_CATALOG[id];
  if (!entry) throw new Error(`routeMutationCatalog: unknown mutation '${id}'`);
  return entry;
}

/**
 * The query keys one mutation invalidates.
 *
 * Extracted from the hook for the same reason routeResourceQuery exists: this
 * repository's vitest environment is `node` with no renderer, so anything left
 * inside a hook body cannot be tested at all. The decision this function makes
 * — invalidate by the SINGLE-SEGMENT PREFIX `[resourceId]`, not by a rebuilt
 * full key — is the load-bearing one, and here it is reachable from a plain
 * test.
 *
 * A single segment is what makes the match cross every scope and every params
 * combination; see useRouteMutation's header for why both of those are
 * intended rather than tolerated. Building a longer key here would narrow the
 * match silently, and "invalidated fewer entries than it claimed" is precisely
 * the failure the static guard cannot see and this function's test can.
 */
export function mutationInvalidationKeys(id: string): readonly (readonly string[])[] {
  return getMutation(id).invalidates.map((resourceId) => Object.freeze([resourceId]));
}
